import { filter, uniq } from 'lodash';
import moment from 'moment-timezone';

import * as ServiceActions from './ServiceActions';

const initialState = {
  variations: [],
  completedVariations: [],
  pricings: [],
};

export default function service(state = initialState, action) {
  switch (action.type) {
    case 'UNAUTHORIZED':
      return {
        ...initialState
      };
    case 'LOGOUT':
      return {
        ...initialState
      };
    case ServiceActions.LOAD_VARIATIONS:
      const originalVariations = action.data.variations
      let filteredVariations = originalVariations
      const shouldFilterVariationsOnlyForKiosk = originalVariations.some(variation => variation.available_on_kiosk && !variation.add_on)
      if (shouldFilterVariationsOnlyForKiosk) {
        filteredVariations = originalVariations.map(variation => ({...variation, hidden: !variation.available_on_kiosk}))
      }
      return {
        ...state,
        variations: filteredVariations
          .sort((v1, v2) => v1.global_sort_order - v2.global_sort_order)
      };
    case ServiceActions.LOAD_PRICINGS:
      return {
        ...state,
        pricings: action.data.pricings
      };
    case ServiceActions.LOAD_COMPLETED_VARIATIONS:
      return {
        ...state,
        completedVariations: action.data
      };
    default:
      return state;
  }
}

export function calculatePricings(
  variations,
  workingHours,
  customHours,
  employees,
  pricings,
  isNewClient,
  timeZone,
  selectedVariations
) {
  const availabilityMatrix = createAvailabilityMatrix({
    WorkingHours: workingHours,
    CustomHours: customHours,
    timeZone
  });

  return variations.map(v => ({
    variationId: v.id,
    ...getVariationPrice(
      variations,
      pricings,
      employees,
      v.id,
      isNewClient,
      availabilityMatrix,
      selectedVariations
    )
  }));
}

export function getBookableVariations(variations) {
  return variations.filter((v) => !v.deleted_at && v.bookable)
}

export function getVariationsWithAddons(state) {
  const bookableVariations = getBookableVariations(state.service.variations)
  const mainVariations = bookableVariations.filter(
    s => s.add_on === false
  )

  mainVariations.forEach(v => {
    const addons = bookableVariations.filter(
      a =>
        a.add_on &&
        a.add_on_variation_connections.filter(
          ac => ac.parent_variation_id === v.id
        ).length > 0
    );

    v.addons = addons;
  });

  return mainVariations;
}

export function getVariationPrice(
  variations,
  pricings,
  employees,
  variationId,
  isNewClient,
  availabilityMatrix,
  selectedVariations
) {
  const bookableEmployees = getBookableEmployees({
    Employees: employees,
    Pricings: pricings,
    Variations: variations,
    availabilityMatrix: availabilityMatrix,
    variationIds: [variationId, ...selectedVariations.map(v => v.variationId)]
  });

  const pricing = pricings
    .filter(p => p.variation_id === variationId)
    .filter(
      p => bookableEmployees.filter(e => e.id === p.employee_id).length > 0
    );

  var minPrice = null;
  var maxPrice = null;

  pricing.forEach(p => {
    let price = isNewClient ? p.new_list_price : p.existing_list_price;
    if (maxPrice == null || price > maxPrice) maxPrice = price;
    if (minPrice == null || price < minPrice) minPrice = price;
  });

  return {
    minPrice,
    maxPrice
  };
}

// Vlad's code

// export const getIdsFromVariations = ({ variationIds, Pricings, Employees }) => {
//   return variationIds.reduce((employeeIds, id) => {
//     let employeeIdsForVariation = Pricings.query({ variationId: id }).map(p => {
//       const employee = Employees.byId(p.employeeId);
//       if (!employee) return undefined;
//       return employee.id;
//     });

//     employeeIdsForVariation = compact(uniq(employeeIdsForVariation));

//     // first iteration, don't have any prev result to interesect our array with
//     if (!employeeIds) {
//       return employeeIdsForVariation;
//     }

//     return intersection(employeeIds, employeeIdsForVariation);
//   }, null);
// };

export const getEmployeeVariationIds = ({ employee, Pricings }) => {
  const employeePricings = Pricings.filter(p => p.employee_id === employee.id);

  return uniq(employeePricings.map(p => p.variation_id));
};

// Get a date in timezone based on a working hour or custom hour
// param toLocale:boolean - ch needs hour conversion from utc to locale, wh doesn't
const dateStringToLocalDate = (date, tz, toLocale = false) => {
  let dateObj = moment(date);
  dateObj = toLocale ? dateObj.tz(tz) : dateObj.utc();

  let nowInTz = moment.tz(tz);
  return nowInTz.set({
    hour: dateObj.hour(),
    minute: dateObj.minute(),
    second: 0,
    millisecond: 0
  });
};

// Is the employee working today or not?!
export const readAvailabilityMatrix = (matrix, employeeId) => {
  if (matrix.ch[employeeId] == null) {
    if (matrix.ch.allAvailable) {
      return true;
    } else if (matrix.ch.allDayOff || matrix.ch.noneAvailable) {
      return false;
    } else if (matrix.wh[employeeId]) {
      return matrix.wh[employeeId];
    } else {
      // No wh or ch means he's not working
      return false;
    }
  } else {
    return matrix.ch[employeeId];
  }
};

export const createAvailabilityMatrix = ({
  WorkingHours,
  CustomHours,
  timeZone
}) => {
  let now = moment.tz(timeZone);
  let dayAccr = now.format('dd').toLowerCase();
  let matrix = {
    wh: {},
    ch: {
      allDayOff: false,
      allAvailable: false,
      noneAvailable: false
    }
  };

  WorkingHours.forEach(wh => {
    if (!wh[dayAccr]) {
      return;
    } // Not current day

    if (now < dateStringToLocalDate(wh.close_time, timeZone)) {
      matrix.wh[wh.employee_id] = true;
    }
  });

  CustomHours.forEach(ch => {
    // eslint-disable-next-line
    if (ch.date != now.format('YYYY-MM-DD')) {
      return;
    } // Not current day

    if (ch.non_working_day) {
      // Not a working day
      if (ch.employee_id) {
        matrix.ch[ch.employee_id] = false;
      } else {
        matrix.ch.all_day_off = true;
      }
    } else if (now < dateStringToLocalDate(ch.close_at, timeZone, true)) {
      // Still a working day
      if (ch.employee_id) {
        matrix.ch[ch.employee_id] = true;
      } else {
        // Multiple custom hours during day
        matrix.ch.noneAvailable = false;
        matrix.ch.allAvailable = true;
      }
    } else if (!matrix.ch[ch.employee_id]) {
      // Working day is over and there wasn't a previous ch which indicated an availability
      if (ch.employee_id) {
        matrix.ch[ch.employee_id] = false;
      } else {
        matrix.ch.noneAvailable = true;
      }
    }
  });

  return matrix;
};

export const getBookableEmployees = ({
  Employees,
  Pricings,
  Variations,
  availabilityMatrix = null,
  variationIds = null
}) => {
  return Employees.filter(employee => {
    let bookable = availabilityMatrix
      ? readAvailabilityMatrix(availabilityMatrix, employee.id)
      : true;

    const employeeVariationIds = getEmployeeVariationIds({
      employee,
      Pricings,
      Variations
    });

    // Can Perform Certain Variation
    if (variationIds) {
      bookable =
        bookable &&
        variationIds.reduce(
          (ok, id) => ok && employeeVariationIds.indexOf(id) !== -1,
          true
        );
    }

    // Can Perform Any Variation
    bookable = bookable && employeeVariationIds.length > 0;

    return bookable;
  });
};

// export const getBookableEmployeesWithVariationIds = ({
//   Employees,
//   Pricings,
//   Variations
// }) => {
//   return Employees.all().reduce((filtered, employee) => {
//     const variationIds = getEmployeeVariationIds({
//       employee,
//       Pricings,
//       Variations
//     });

//     if (variationIds.length > 0) {
//       filtered.push({ employee, variationIds });
//     }

//     return filtered;
//   }, []);
// };
